// src/pages/SquadsPage.tsx
//
// Registry de Squads — o quarto ativo do RAIOS, ao lado de Agentes, Skills e
// Pipelines. Segue a mesma anatomia da tela de Skills (cards .sklc + .dfilters +
// detalhe em modal com deep-link) porque é o mesmo tipo de tela; inventar um
// segundo padrão faria a mesma coisa existir duas vezes no produto.
//
// FILTROS SÃO SERVER-SIDE. Diferente de Skills (onde busca e status filtram no
// cliente), a rota /v1/squads aceita scope, status, search e paginação — então
// filtrar aqui traria a página 1 do servidor e filtraria só ELA, escondendo os
// resultados das páginas seguintes. Paginação server-side + filtro client-side
// é uma combinação que mente sobre o total.
//
// NÃO HÁ BOTÃO "Nova squad". Squad se cria conversando com o Companion
// (`create_squad`), que monta o SQUAD.md, o roster e o charter pela fábrica
// VIBRATE. Um formulário aqui seria uma segunda rota de criação — exatamente o
// que o RAIOS não deve ter.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type Squad, type SquadOrchestrator, type SquadScope } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Users, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import '@/styles/wf-agents.css'; // chrome compartilhado: .dfilters/.search
import '@/styles/wf-skills.css'; // anatomia dos cards: .skl-grid/.sklc/.skl-bdg
import { SquadCard } from '@/components/squads/SquadCard';
import { SquadDetailModal } from '@/components/squads/SquadDetailModal';
import { SCOPE_FILTER_OPTIONS, STATUS_FILTER_OPTIONS } from '@/components/squads/squad-constants';

export interface SquadsPageProps {
  /** Deep-link /squads/:id — abre o modal de detalhe sobre a lista. */
  detailId?: string;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

export function SquadsPage({ detailId }: SquadsPageProps) {
  const navigate = useNavigate();
  // Anti-bug Radix: ao fechar o menu kebab (Portal), o "click fantasma"
  // pós-pointerup cai no card por baixo e abriria o detalhe.
  const suppressCardNavRef = useRef(false);

  const [squads, setSquads] = useState<Squad[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState<SquadScope | ''>('');
  const [statusFilter, setStatusFilter] = useState('');
  const [orchestratorFilter, setOrchestratorFilter] = useState('');
  // Opções vindas do DADO — só orquestradores que de fato coordenam alguma
  // squad. Uma lista fixa ofereceria filtros que não filtram nada.
  const [orchestrators, setOrchestrators] = useState<SquadOrchestrator[]>([]);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);

  // Debounce da busca: cada tecla vira uma requisição sem isso, e o resultado
  // que chega por último nem sempre é o da última tecla digitada.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setSearch(searchInput);
      setCurrentPage(1);
    }, 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    let cancelado = false;
    api
      .listSquadOrchestrators()
      .then((o) => {
        if (!cancelado) setOrchestrators(o);
      })
      .catch(() => {
        // Filtro indisponível não derruba a listagem: o select fica só com
        // "todos" e o resto da tela continua útil.
        if (!cancelado) setOrchestrators([]);
      });
    return () => {
      cancelado = true;
    };
  }, []);

  useEffect(() => {
    let cancelado = false;
    const carregar = async () => {
      setLoading(true);
      setError('');
      try {
        const page = await api.listSquads({
          scope: scopeFilter || undefined,
          status: statusFilter || undefined,
          search: search.trim() || undefined,
          orchestrator: orchestratorFilter || undefined,
          page: currentPage,
          per_page: pageSize,
        });
        if (cancelado) return;
        setSquads(page.data ?? []);
        setTotal(page.total ?? 0);
      } catch (err) {
        if (!cancelado) setError(err instanceof Error ? err.message : 'Erro ao carregar squads');
      } finally {
        if (!cancelado) setLoading(false);
      }
    };
    carregar();
    return () => {
      cancelado = true;
    };
  }, [scopeFilter, statusFilter, orchestratorFilter, search, currentPage, pageSize]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const ordenadas = useMemo(
    () =>
      // Ordem alfabética dentro da página, como nas telas de Agentes e Skills.
      [...squads].sort((a, b) =>
        (a.name || a.slug).localeCompare(b.name || b.slug, 'pt-BR', { sensitivity: 'base' }),
      ),
    [squads],
  );

  const onMenuOpenChange = (open: boolean) => {
    if (!open) {
      suppressCardNavRef.current = true;
      window.setTimeout(() => {
        suppressCardNavRef.current = false;
      }, 300);
    }
  };

  const abrirDetalhe = (squad: Squad) => {
    if (suppressCardNavRef.current) return;
    navigate(`/squads/${squad.id}`);
  };

  const temFiltro = Boolean(search.trim() || scopeFilter || statusFilter || orchestratorFilter);

  return (
    <div>
      <PageHeader
        title="Squads"
        description="Times de agentes — SQUAD.md + roster + charter + execuções"
        className="mb-2"
      />

      <div className="dfilters">
        <div className="search" style={{ flex: 1, minWidth: 170 }}>
          <Search className="ic" aria-hidden />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Buscar squad"
            aria-label="Buscar squad"
          />
        </div>
        <select
          value={scopeFilter}
          onChange={(e) => {
            setScopeFilter(e.target.value as SquadScope | '');
            setCurrentPage(1);
          }}
          aria-label="Filtro de scope"
        >
          {SCOPE_FILTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
          aria-label="Filtro de status"
        >
          {STATUS_FILTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {/* Orquestrador — só aparece quando há mais de um. Com um único
            orquestrador o filtro não separa nada e vira ruído na barra. */}
        {orchestrators.length > 1 && (
          <select
            value={orchestratorFilter}
            onChange={(e) => {
              setOrchestratorFilter(e.target.value);
              setCurrentPage(1);
            }}
            aria-label="Filtro de orquestrador"
          >
            <option value="">Orquestrador: todos</option>
            {orchestrators.map((o) => (
              <option key={o.slug} value={o.slug}>
                {o.slug} ({o.squads_count})
              </option>
            ))}
          </select>
        )}
        <select
          value={String(pageSize)}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setCurrentPage(1);
          }}
          aria-label="Squads por página"
        >
          {PAGE_SIZE_OPTIONS.map((n) => (
            <option key={n} value={n}>{n} por página</option>
          ))}
        </select>
      </div>

      {error && (
        <Card className="bg-raios-danger/10 px-4 py-3 mb-4">
          <p className="text-raios-danger-text text-sm">{error}</p>
        </Card>
      )}

      {loading ? (
        <div className="skl-grid">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-44 rounded-xl" />
          ))}
        </div>
      ) : ordenadas.length === 0 ? (
        // Lista vazia POR FILTRO e lista vazia POR AUSÊNCIA dizem coisas
        // diferentes. "Crie sua primeira squad" para quem só digitou um termo
        // que não casou é errado — e esconde que basta limpar o filtro.
        <EmptyState
          icon={Users}
          title={temFiltro ? 'Nenhuma squad para estes filtros' : 'Nenhuma squad ainda'}
          description={
            temFiltro
              ? 'Ajuste a busca, o scope ou o status.'
              : 'Squads são criadas pelo Companion — peça a ele para montar um time para a sua demanda.'
          }
        />
      ) : (
        <>
          <div className="skl-grid">
            {ordenadas.map((s) => (
              <SquadCard
                key={s.id}
                squad={s}
                onClick={abrirDetalhe}
                onMenuOpenChange={onMenuOpenChange}
                onViewDetails={() => navigate(`/squads/${s.id}`)}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-1 py-4">
              <p className="text-xs text-raios-muted">
                Página {currentPage} de {totalPages} · {total} {total === 1 ? 'squad' : 'squads'}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  aria-label="Página anterior"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  aria-label="Próxima página"
                >
                  Próxima
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {detailId && (
        <SquadDetailModal squadId={detailId} open onClose={() => navigate('/squads')} />
      )}
    </div>
  );
}
